import { useEffect, useRef, useState } from 'preact/hooks';

import { GameIframe } from '@/entities/game-iframe/ui';
import { OrientationLocker } from '@/entities/orientation-locker';
import {
  useGameControlsBackButton,
  useHideUI,
  usePlaydeckActions,
  usePlaydeckOpen,
  usePlaydeckState,
  useProgress,
} from '@/entities/playdeck/models/playdeck.store';
import { Method } from '@/entities/playdeck/types';

import { useTelegramBackButton } from '@/shared/hooks/telegram/use-telegram-back-button';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useScreenOrientationUpdater } from '@/shared/hooks/use-orientation/use-screen-orientation-updater';
import { AnalyticsService } from '@/shared/service/analytics/analytics-service';
import { makeGameLoadEndEvent } from '@/shared/service/analytics/events/game-load-end-event';
import { makeGameLoadStartEvent } from '@/shared/service/analytics/events/game-load-start-event';
import { makeGameSessionStartEvent } from '@/shared/service/analytics/events/game-session-start-event';
import { makeSessionStartEvent } from '@/shared/service/analytics/events/session-start-event';
import { useAppState } from '@/shared/stores/app-store/app-store';

import { CirclesScoreModal } from '@/widget/circles-score-modal/ui';
import { DevMenu } from '@/widget/dev-menu/ui';
import { useVerticalSwipeEffect } from '@/widget/panel/hooks/useVerticalSwipeEffect';
import { ScoreModal } from '@/widget/score-modal/ui';

import { useGameLoadingTimer } from '../hooks/use-game-loading-timer';
import { useLoadingMode } from '../hooks/use-loading-mode';
import { useIntl } from '../lib/localization/hooks/use-intl';
import { showSlowLoadingPopup } from '../scripts/slow-loading-popup';
import { LoadingScreen } from './loading-screens/LoadingScreen';

const GAME_STARTED_TIMEOUT_MS = 1000;
const POPUP_TIMEOUT = 30000;

export function App() {
  const { game, startAppArgs, userProfile } = useAppState();
  const pdOpen = usePlaydeckOpen();
  const hideUI = useHideUI();
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const { loadingEndSended, respond } = usePlaydeckState();
  const { setLoadingEnded } = usePlaydeckActions();
  const { isDev } = useFeatureFlag();
  const popupShownRef = useRef(false);
  const { elapsedMs } = useGameLoadingTimer();
  const t = useIntl();
  const progress = useProgress();
  const miniGameEnabled = userProfile?.tests?.miniGame ?? false;
  const miniGameV2Enabled = userProfile?.tests?.miniGameV2 ?? false;
  const gameControlsBackButton = useGameControlsBackButton();

  const { mode: loadingMode, autoStart } = useLoadingMode(
    hideUI,
    miniGameEnabled,
    miniGameV2Enabled
  );

  useTelegramBackButton({ gameControlsBackButton });
  useVerticalSwipeEffect(false);

  // autoStart=true (HideUI): game starts automatically when progress reaches 100%
  useEffect(() => {
    if (!autoStart || progress !== 100 || gameStarted || !game) return;
    AnalyticsService.send(makeGameSessionStartEvent(game.key, startAppArgs?.src, 'auto'));
    setTimeout(() => setGameStarted(true), GAME_STARTED_TIMEOUT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, autoStart]);

  // autoStart=false (Default, AbTest): game starts when PlayButton sets pdOpen=false
  useEffect(() => {
    if (autoStart || pdOpen || gameStarted) return;
    setTimeout(() => setGameStarted(true), GAME_STARTED_TIMEOUT_MS);
  }, [pdOpen, autoStart, gameStarted]);

  useEffect(() => {
    if (!game || !startAppArgs) return;
    AnalyticsService.send(makeSessionStartEvent(game.key, startAppArgs.src));
    AnalyticsService.send(makeGameLoadStartEvent(game));
  }, [game, startAppArgs]);

  useEffect(() => {
    if (elapsedMs > POPUP_TIMEOUT && !popupShownRef.current && !hideUI) {
      popupShownRef.current = true;
      showSlowLoadingPopup(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedMs, t]);

  useEffect(() => {
    if (!game || !respond || progress !== 100 || loadingEndSended) return;
    respond({ method: Method.LOADED });
    AnalyticsService.send(makeGameLoadEndEvent(game.key));
    setLoadingEnded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, respond, game]);

  useScreenOrientationUpdater();

  return (
    <>
      <DevMenu game={game} />
      <ScoreModal />
      <CirclesScoreModal />

      {!gameStarted && (
        <LoadingScreen mode={loadingMode} game={game} pdOpen={pdOpen} isDev={isDev} />
      )}

      {!!game && (
        <>
          <OrientationLocker gameOrientation={game?.orientation} />
          <GameIframe game={game} />
        </>
      )}
    </>
  );
}
